import { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  // CASING CONVENTION: `children` should be a Title Case action label
  // Examples: "View Your Badges", "Explore This Song", "Sign In"
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive';
  fullWidth?: boolean;
}

export default function Button({
  children,
  variant = 'primary',
  fullWidth = false,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  // Base styles - pill shape, transitions, minimum 44px tap target
  const baseStyles = 'font-semibold rounded-full transition-all duration-200 items-center justify-center gap-2 focus:outline-none min-h-[44px] px-7 text-[14px] whitespace-nowrap';

  // UX Gold Standard variants - simple, predictable, calm
  const variantStyles = {
    // Primary: Teal filled pill (main action on screen)
    primary: 'bg-[#40E0D0] text-[#031414] hover:bg-[#47EAD8] active:bg-[#40E0D0] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#40E0D0] shadow-sm focus-visible:ring-2 focus-visible:ring-[#40E0D0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#111111]',

    // Secondary: Stroke outline pill (alternative important action)
    secondary: 'border border-[#40E0D0] text-[#40E0D0] bg-transparent hover:bg-[#40E0D0]/10 active:bg-[#40E0D0]/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent focus-visible:bg-[#40E0D0]/10 focus-visible:ring-2 focus-visible:ring-[#40E0D0]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111111]',

    // Tertiary: Text button, no pill (low priority actions)
    tertiary: 'text-[#A0A0A0] bg-transparent hover:text-[#B5B5B5] active:text-[#808080] disabled:opacity-40 disabled:cursor-not-allowed px-0 focus-visible:text-[#B5B5B5]',

    // Destructive: Red filled pill (dangerous/irreversible actions)
    destructive: 'bg-[#FF6F61] text-white hover:bg-[#FF7D6F] active:bg-[#FF6F61] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#FF6F61] focus-visible:ring-2 focus-visible:ring-[#FF6F61] focus-visible:ring-offset-2 focus-visible:ring-offset-[#111111]',
  };

  const displayStyles = fullWidth ? 'flex w-full' : 'inline-flex';

  return (
    <button
      className={`${baseStyles} ${displayStyles} ${variantStyles[variant]} ${className}`}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}
